import { v4 as uuid } from "uuid"
import { SchemaFieldVariant } from "./schema.d"
/**
 * IMPORTANT TO NOTE!
 * Esri is strict with it's field defininitions, so there's
 * no way to store arbitrary meta against a field, i.e. the
 * field's variant (markdown, select, etc). Due to this,
 * I've decided to store the variant as part of the field
 * name. This can be stripped for management purposes. So a
 * required multi select field in the first place would look like:
 * Category:select:multiselect:true:0
 *
 * Also note: I bloody hate this solution. It's the easiest
 * I could come up with without mirroring the field schema
 * in another database which is unnecessary. If you have
 * problems with it please bring them up with me; we can
 * vent together.
 *
 * Because of this, I've done my absolute best to comment
 * everything as best as possible!!
 */

import { SchemaField } from "./schema"

/**
 * Gets a property hard-coded into the field name.
 * We have to do this because esri doesn't allow arbitrary information
 * stored against a field.
 *
 * @param property property to get from the alias
 * @param alias the alias of the field
 */
export const getPropertyFromAlias = (
  property: "variant" | "subvariant" | "required" | "order",
  alias: string | undefined
) => {
  const parts = alias?.split(":") || []
  switch (property) {
    case "variant":
      return parts[1]
    case "subvariant":
      return parts[2]
    case "required":
      return parts[3] === "required"
    case "order":
      return parts[4]
  }
}

/**
 * Strips the variant from the alias when it comes down from esri
 * @param alias The raw alias from esri
 */
export const stripAdditionalPropertiesFromAlias = (alias: string) =>
  alias.split(":")[0]

/**
 * Generates an esri/request REST URL based on the layer's URL
 * @param layer The layer we're looking to perform REST operations against
 */
export const getFeatureLayerRestUrl = (layer: __esri.FeatureLayer) =>
  layer?.url.replace("rest/services", "rest/admin/services") + "/0"

/** The types of changes we can make to the layer definition */
type DefinitionMethod =
  | "addToDefinition"
  | "updateDefinition"
  | "deleteFromDefinition"

export const pushDefinitionChangesToEsri = (
  updatedFields: SchemaField[] = [],
  schema: SchemaField[] | undefined,
  layer: __esri.FeatureLayer | undefined,
  request: typeof __esri.request | undefined
) => {
  if (layer && request && schema) {
    /** Store array of promises */
    const apiRequests: Promise<__esri.RequestResponse>[] = []
    const featureLayerRestUrl = getFeatureLayerRestUrl(layer)

    /** Find fields to add */
    const currentFieldNames = schema?.map(field => field.name)
    const fieldsToAdd = updatedFields.filter(
      field => !currentFieldNames?.includes(field.name)
    )

    /** If there are fields to add, add a promise to the list */
    if (fieldsToAdd.length) {
      // create an esri request to add the fields
      const addFields = request(
        featureLayerRestUrl + "/addToDefinition",
        generateRequestOptions("addToDefinition", fieldsToAdd)
      )
      // add it to the list of promises to wait for
      apiRequests.push(addFields)
    }

    /** Find fields to remove */
    const updatedFieldNames = updatedFields.map(field => field.name)
    const fieldsToDelete = schema?.filter(
      field => !updatedFieldNames.includes(field.name)
    )
    if (fieldsToDelete.length) {
      // create an esri request to remove the fields
      const deleteFields = request(
        featureLayerRestUrl + "/deleteFromDefinition",
        generateRequestOptions("deleteFromDefinition", fieldsToDelete)
      )

      // add it to the list of promises to wait for
      apiRequests.push(deleteFields)
    }

    /** Find fields to update */
    const fieldsToUpdate = updatedFields.filter(fieldUpdate => {
      const matchingField = schema?.find(
        field => fieldUpdate.name === field.name
      )

      /** Check if updates match */
      return compareFieldUpdateProperties(fieldUpdate, matchingField)
    })

    if (fieldsToUpdate?.length) {
      // create an esri request to update the fields
      const updateFields = request(
        featureLayerRestUrl + "/updateDefinition",
        generateRequestOptions("updateDefinition", fieldsToUpdate)
      )
      // add it to the list of promises to wait for
      apiRequests.push(updateFields)
    }

    return Promise.all(apiRequests)
  } else {
    return Promise.reject(
      "layer, schema and request are required parameters for pushDefinitionChangesToEsri"
    )
  }
}

/**
 * Compares updates with field definition
 * @param fieldUpdate updates to the field
 * @param originalField original field definition
 */
const compareFieldUpdateProperties = (
  fieldUpdate: SchemaField,
  originalField: SchemaField | undefined
) => {
  return (
    originalField &&
    Object.entries(fieldUpdate).reduce((hasChanges, [key, updateValue]) => {
      /** Grab the original field value */
      let fieldValue = originalField[key as keyof SchemaField]

      /** Description comes through as undefined from esri, cheers */
      if (key === "description" && fieldValue === undefined) fieldValue = ""

      /** Diff the update */
      return updateValue !== fieldValue ? true : hasChanges
    }, false)
  )
}

/**
 * Generates an esri/request options object
 * @param method The update type we're making to the layer definition
 * @param fields The fields we're updating
 */
const generateRequestOptions = (
  method: DefinitionMethod,
  fields: SchemaField[]
): __esri.RequestOptions => ({
  responseType: "json",
  authMode: "immediate",
  method: "post",
  body: generateRequestBody(method, fields),
})

/**
 * Generates an esri/request body object
 * @param method the update type we are making to the layer defintition
 * @param fields The fields we're performing this update on
 */
const generateRequestBody = (
  method: DefinitionMethod,
  fields: SchemaField[]
) => {
  const body = new FormData()
  body.append("f", "json")
  body.append(
    method,
    JSON.stringify({
      fields: schemaToEsriFields(fields),
    })
  )

  return body
}

/**
 * Converts esri field definitions to dna schema friendly definitions
 * @param fields Esri fields from the API
 */
export const esriToSchemaFields = (fields: __esri.Field[]) =>
  fields
    .filter(field => field.name.includes("dna_"))
    .map(
      (dnaField, index) =>
        ({
          name: dnaField.name.replace("dna_", ""),
          description: dnaField.description,
          // the variant, subvariant & required is stored as part of the alias, so we strip it here, add it on submit
          variant: getPropertyFromAlias("variant", dnaField.alias) || "text",
          subvariant:
            getPropertyFromAlias("subvariant", dnaField.alias) || undefined,
          required: getPropertyFromAlias("required", dnaField.alias) || false,
          // see above
          alias: stripAdditionalPropertiesFromAlias(dnaField.alias),
          options: dnaField
            .toJSON()
            .domain?.codedValues?.map((value: any) => value.name)
            .join(", "),
          order: getPropertyFromAlias("order", dnaField.alias) || index,
        } as SchemaField)
    )

/**
 * Converts the fields from our format (with a variant) to esri-friendly
 * @param fields the fields we're santitising for esri
 */
const schemaToEsriFields = (fields: SchemaField[]): __esri.FieldProperties[] =>
  // @ts-ignore - ignoring the 'type' - the API requires esriFieldTypeString but __esri.FieldProperties wants just string.
  fields.map(
    ({
      alias,
      name,
      variant,
      subvariant,
      required,
      order,
      description,
      options,
      ...field
    }) => {
      const fieldValues = {
        ...field,
        description: JSON.stringify({
          value: description || "",
          fieldValueType: getEsriFieldValueType(variant),
        }),
        /**
         * Really important to note here - we can't store arbitrary
         * information against a field. So we've got the unfortunate
         * solution of appending information to the field title,
         * splitting them with :
         */
        alias: `${alias}:${variant}:${subvariant || ""}:${
          required ? "required" : ""
        }:${order || ""}`,
        /**
         * Similarly, we identify dna fields with a dna_ prefix,
         * though the user need not know this.
         */
        name: "dna_" + name,
        length: getEsriFeildLength(variant),
        type: getEsriFieldType(variant),
      }

      /** If there are field options, push them on */
      if (options) {
        fieldValues.domain = getEsriFieldDomain(options, alias)
      }

      return fieldValues
    }
  )

/**
 * Convert the field variant to an esri field type
 * @param variant the field variant
 */
export const getEsriFieldType = (variant: SchemaFieldVariant) => {
  switch (variant) {
    case "switch":
      return "esriFieldTypeInteger"
    default:
      return "esriFieldTypeString"
  }
}

/**
 * Returns a field length depending on the variant
 * @param variant the field variant
 */
export const getEsriFeildLength = (variant: SchemaFieldVariant) => {
  switch (variant) {
    case "switch":
      return 1
    case "multiline":
      return 512
    case "markdown":
      return 2048
    default:
      return 256
  }
}

/**
 * Returns a field value type based on the description
 * @param variant the field variant
 */
export const getEsriFieldValueType = (variant: SchemaFieldVariant) => {
  switch (variant) {
    case "switch":
      return "Binary"
    case "text":
      return "Name or Title"
    case "multiline":
    case "markdown":
      return "Description"
    case "select":
      return "Type or Category"
    default:
      return ""
  }
}

/**
 * Converts an array of strings into esri coded values.
 * @param options array of strings to turn into options
 * @param alias the alias of the field (for naming)
 */
export const getEsriFieldDomain = (
  options: string | undefined,
  alias: string | undefined
) => {
  if (!options) return undefined
  const optionsArray = options.split(",")
  // @ts-ignore - type is expecting "coded-value" but api wants "codedValue"
  return {
    name: alias + uuid() + "codedValues",
    type: "codedValue",
    codedValues: optionsArray
      .filter(v => v)
      .map(option => ({
        code: option.trim(),
        name: option.trim(),
      })),
  } as __esri.DomainProperties
}
